import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
// import { useLoginMutation, getAccessToken } from '../services/auth';
import routes from '../config/routes';
import { getDomainLogo, getDomainAppUrl } from '../utils/domainConfig';
import { useLoginMutation } from '../services/auth/authApi';
import loginBg from '../assets/loginbg.png';

const logo = getDomainLogo();

const getPanel = () => {
  const parts = window.location.hostname.split('.');
  return parts.length > 2 ? parts[0] : 'admin';
};

const LoginPage = () => {
  const navigate = useNavigate();
  const [login, { isLoading, error: loginError }] = useLoginMutation();

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    appUrl: getDomainAppUrl(),
    panel: getPanel(),
  });
  const [localError, setLocalError] = useState('');

  // Redirect if already authenticated
  // useEffect(function() {
  //   if (accessToken) {
  //     navigate(routes.DASHBOARD);
  //   }
  // }, [accessToken, navigate]);

  const handleChange = function(e) {
    const name = e.target.name;
    const value = e.target.value;
    
    // Clear error when user starts typing
    if (loginError) {
      setLocalError('');
    }
    if (localError) {
      setLocalError('');
    }
    
    setFormData(function(prev) {
      return {
        ...prev,
        [name]: value
      };
    });
  };

  const handleSubmit = async function(e) {
    e.preventDefault();
    
    


    
    try {
      const result = await login(formData).unwrap();
      console.log('Login result:', result);
  
      // Check for both success and status fields (API returns status: true)
      if (result.success || result.status) {
        // Store token if available
        if (result.token) {
          localStorage.setItem('token', result.token);
        }
        // Store user data - check both result.user and result.data
        const userData = result.user || result.data;
        if (userData) {
          // Map userType to hierarchyLevel if hierarchyLevel is not present
          if (!userData.hierarchyLevel && userData.userType) {
            const userTypeLevelMap = {
              'Client': 1,
              'Agent': 2,
              'SuperMaster': 3,
              'Master': 4,
              'MiniAdmin': 5,
              'Admin': 6,
              'SuperAdmin': 7,
              'Finance': 1,
            };
            userData.hierarchyLevel = userTypeLevelMap[userData.userType] || 0;
          }
          
          console.log('Storing user data:', userData);
          localStorage.setItem('user_data', JSON.stringify(userData));
          
          // Dispatch storage event to refresh components
          window.dispatchEvent(new StorageEvent('storage', {
            key: 'user_data',
            newValue: JSON.stringify(userData)
          }));
        }
        
        // Force navigation with replace to avoid back button issues
        navigate(routes.DASHBOARD, { replace: true });
        
        // Fallback: force page reload to dashboard if navigation doesn't work
        setTimeout(() => {
          if (window.location.pathname === '/login') {
           window.location.replace(routes.DASHBOARD);
          }
        }, 100);
      } else {
        setLocalError(result.message || 'Login failed');
      }
    } catch (error) {
      console.log('Login error:', error);
      setLocalError(error?.data?.message || error?.message || 'Login failed');
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-4 bg-[#f5f5f7]"
      style={{
        backgroundImage: `url(${loginBg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'top center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <div className="w-full max-w-[400px] animate-fadeInUp">
        {/* clean card */}
        <div className="rounded-2xl bg-white border border-[#e5e5e5] shadow-2xl overflow-hidden">
          {/* top accent line */}
          <div className="h-1 bg-gradient-to-r from-[var(--primary)] via-[var(--primary)] to-[var(--accent)]" />

          <div className="flex justify-center pt-7 pb-2">
            <img alt="logo" src={logo} className="h-[90px]" />
          </div>

          <div className="px-7 pb-7">
            <h1 className="text-center text-[#1a1a2e] font-bold text-xl mb-1">Welcome back</h1>
            <p className="text-center text-[#6b7280] text-xs mb-6">Sign in to your admin panel</p>

            {/* Error Message */}
            {(localError || loginError) && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-600 rounded-lg text-sm flex items-center gap-2 animate-slideDown">
                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20" className="flex-shrink-0">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                </svg>
                <span>{localError || loginError?.message}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Username */}
              <div className="relative">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#9ca3af]">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                  </svg>
                </span>
                <input
                  type="text"
                  name="username"
                  placeholder="Username"
                  value={formData.username}
                  onChange={handleChange}
                  required
                  disabled={isLoading}
                  className="w-full pl-10 pr-3 py-2.5 bg-[#f7f7f8] border border-[#e5e5e5] rounded-lg text-sm text-[#1a1a2e] placeholder:text-[#9ca3af] transition-all focus:outline-none focus:border-[var(--primary)] focus:ring-2 focus:ring-[var(--primary)]/30 disabled:bg-[#eeeeee] disabled:cursor-not-allowed"
                />
              </div>

              {/* Password */}
              <div className="relative">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#9ca3af]">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                  </svg>
                </span>
                <input
                  type="password"
                  name="password"
                  placeholder="Password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  disabled={isLoading}
                  autoCapitalize="none"
                  autoCorrect="off"
                  autoComplete="current-password"
                  className="w-full pl-10 pr-3 py-2.5 bg-[#f7f7f8] border border-[#e5e5e5] rounded-lg text-sm text-[#1a1a2e] placeholder:text-[#9ca3af] transition-all focus:outline-none focus:border-[var(--primary)] focus:ring-2 focus:ring-[var(--primary)]/30 disabled:bg-[#eeeeee] disabled:cursor-not-allowed"
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full rounded-lg bg-[var(--accent)] hover:bg-[var(--accent-3)] text-white font-semibold py-2.5 px-4 transition-colors duration-200 shadow-sm shadow-[var(--accent)]/30 disabled:bg-[#e5b7c8] disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isLoading ? (
                  <React.Fragment>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Logging in...
                  </React.Fragment>
                ) : (
                  'Login'
                )}
              </button>
            </form>
          </div>
        </div>

        <div className="bg-white/90 backdrop-blur-sm rounded-xl px-4 py-2.5 mt-5 shadow-sm">
          <div className="text-center font-semibold text-sm text-[#6b7280]">
            Note — This Website is not for Indian Territory
          </div>
          <div className="text-red-600 text-center text-base font-bold mt-1">
            18+ Only
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
